import { fromCents, toCents } from "../totals";

export const toFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const asArray = (value) => {
  const parsed = parseJsonValue(value);
  if (Array.isArray(parsed)) return parsed;
  return parsed === null || parsed === undefined || parsed === "" ? [] : [parsed];
};

const firstString = (...values) => {
  for (const value of values.flatMap((entry) => asArray(entry))) {
    const candidate = typeof value === "string"
      ? value
      : value?.Url || value?.URL || value?.url || value?.ImageUrl || value?.imageUrl;
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
  }
  return "";
};

export const getCustomProductPriceBreak = (product = {}) => {
  const sources = [product.Prices, product.prices, product.LowestPrice, product.lowestPrice];
  return sources.flatMap(asArray).find((price) => price && typeof price === "object") || {};
};

const resolveQuantity = (price) => {
  const raw = price?.Quantity ?? price?.quantity;
  const candidate = raw && typeof raw === "object"
    ? raw.From ?? raw.from
    : raw;
  const quantity = toFiniteNumber(candidate, 1);
  return quantity > 0 ? quantity : 1;
};

const priceFromMargin = (cost, margin) => {
  if (!(cost > 0) || !(margin >= 0 && margin < 100)) return 0;
  return fromCents(toCents(cost / (1 - margin / 100)));
};

const safeObject = (value) => {
  const parsed = parseJsonValue(value);
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? { ...parsed } : {};
};

// CustomProduct writes its selectable options into Attributes/Sku/Variants.
// The cart also needs simple Colors/Sizes arrays for its initial Edit Item UI.
const optionName = (entry) => {
  if (typeof entry === "string" || typeof entry === "number") return String(entry).trim();
  return String(entry?.Name ?? entry?.name ?? entry?.Value ?? entry?.value ?? "").trim();
};

const uniqueOptionNames = (entries) => Array.from(new Map(
  entries.map(optionName).filter(Boolean).map((value) => [value.toLowerCase(), value])
).values());

const storedCustomOptions = (product, type) => {
  const isColor = type === "color";
  const codes = isColor ? new Set(["COLOR", "COLOUR", "PRCL", "COLR"]) : new Set(["SIZE"]);
  const bucketNames = isColor ? new Set(["color", "colors", "colour", "colours"]) : new Set(["size", "sizes"]);
  const attributes = safeObject(product.Attributes ?? product.attributes);
  const variants = asArray(product.Variants ?? product.variants);
  const skuRows = asArray(product.Sku ?? product.sku);
  const topLevel = isColor ? product.Colors ?? product.colors : product.Sizes ?? product.sizes;
  const bucketValues = Object.entries(attributes)
    .filter(([key]) => bucketNames.has(key.toLowerCase()))
    .flatMap(([, value]) => asArray(value?.Values ?? value));
  const variantValues = variants.flatMap((variant) => {
    const direct = isColor
      ? variant?.Color ?? variant?.Colour ?? variant?.color ?? variant?.colour
      : variant?.Size ?? variant?.size;
    const nested = asArray(variant?.Attributes).filter((attribute) =>
      codes.has(String(attribute?.type ?? attribute?.Type ?? attribute?.Code ?? "").trim().toUpperCase())
    ).map((attribute) => attribute?.value ?? attribute?.Value ?? attribute?.Name);
    return [direct, ...nested];
  });
  const skuValues = skuRows.flatMap((sku) =>
    asArray(sku?.Values).filter((value) =>
      codes.has(String(value?.Code ?? "").trim().toUpperCase())
    )
  );
  return uniqueOptionNames([...asArray(topLevel), ...bucketValues, ...variantValues, ...skuValues]);
};

export const normalizeCustomProductForOrder = (product, context = {}) => {
  if (!product || typeof product !== "object") {
    throw new TypeError("A custom product is required.");
  }

  const rawId = product.Id ?? product.id ?? product.RawProductId;
  if (rawId === undefined || rawId === null || String(rawId).trim() === "") {
    throw new TypeError("Custom product is missing an ID.");
  }

  const price = getCustomProductPriceBreak(product);
  const quantity = resolveQuantity(price);
  const cost = Math.max(0, toFiniteNumber(price.Cost ?? price.cost, 0));
  const historicalPrice = Math.max(0, toFiniteNumber(price.Price ?? price.price, 0));
  const requestedMargin = toFiniteNumber(context.defaultMargin ?? product.Margin, 40);
  const margin = requestedMargin >= 0 && requestedMargin < 100 ? requestedMargin : 40;
  // A positive supplier cost continues through normal margin pricing. A legacy
  // client price is used only when no usable supplier cost exists.
  const retail = cost > 0 ? priceFromMargin(cost, margin) : historicalPrice;
  const total = fromCents(toCents(retail) * quantity);
  const supplier = product.Supplier && typeof product.Supplier === "object" ? product.Supplier : {};
  const supplierName = product.SupplierName ?? product.supplierName ?? supplier.Name ?? supplier.name ??
    (typeof product.Supplier === "string" ? product.Supplier : "");
  const explicitInternalSupplierId = product.SupplierId ?? product.supplierId ??
    product.InternalSupplierId ?? product.internalSupplierId ?? null;
  const embeddedLegacySupplierId = supplier.Id ?? supplier.id ?? product.ExternalSupplierId ?? null;
  const sku = context.defaultSku || product.SKU || product.sku || product.Number ||
    product.ProductCode || String(rawId);
  const productId = context.productId || `CUSTOM-${rawId}`;
  const colors = storedCustomOptions(product, "color");
  const sizes = storedCustomOptions(product, "size");

  return {
    ...product,
    ProductId: productId,
    RawProductId: String(rawId),
    Id: rawId,
    Source: "custom",
    LookupType: "custom",
    SKU: String(sku),
    Number: String(sku),
    ImageUrl: firstString(product.ImageUrl, product.imageUrl, product.Images, product.images) || null,
    Supplier: supplierName,
    // Embedded Supplier.Id values on historical custom products are external
    // metadata, not verified Suppliers.id foreign keys.
    SupplierId: explicitInternalSupplierId,
    SupplierName: supplierName || null,
    ExternalSupplierId: embeddedLegacySupplierId != null ? String(embeddedLegacySupplierId) : null,
    SupplierSource: "custom",
    Options: safeObject(product.Options ?? product.options),
    Variants: asArray(product.Variants ?? product.variants),
    Colors: colors,
    Sizes: sizes,
    selectedColor: colors[0] || "",
    Size: sizes[0] || "",
    CurrencyCode: price.CurrencyCode ?? price.currencyCode ?? product.CurrencyCode ?? product.Currency ?? "CAD",
    Quantity: quantity,
    Cost: cost,
    Retail: retail,
    ClientPrice: retail,
    Total: total,
    Subtotal: total,
    Margin: margin,
    Imprinting: product.Imprinting,
    ...(context.taxId != null ? { Tax: context.taxId } : {}),
  };
};
